import { GraphQLServerError } from "~/common/graphql/GraphQLServerError";
import { logError } from "~/common/logging";
import { commonResx } from "../../resxDist/commonResx";
import { apiMiddlewareActionTypes } from "../Middleware/apiMiddlewareActionTypes";
import { ApiErrorTypes, ObjectNotFoundErrorBody, UserFriendlyExcBody } from "./index";

export type ObjectNotFoundAction = ReturnType<typeof objectNotFoundApiError>;
export type UserFriendlyErrorAction = ReturnType<typeof userFriendlyApiError>;
export type UnknownErrorAction = ReturnType<typeof unknownApiError>;

type ErrorHandlingOptions = {
    withoutReInit?: boolean;
    callback?: Fn<any>;
    message?: string;
};

export type HandlingConfiguration = (errorType: ApiErrorTypes, body: any) => ErrorHandlingOptions;

export const getResponseBody = (xhr: Partial<JQuery.jqXHR>): any => xhr && xhr.responseJSON;
export const getErrorType = (xhr: Partial<JQuery.jqXHR>): ApiErrorTypes => getResponseBody(xhr)?.ErrorType;
const getOptions = (configuration?: HandlingConfiguration, errorType?: ApiErrorTypes, body?: any) =>
    configuration?.(errorType, body) || undefined;

export const createApiError = (
    xhr: Partial<JQuery.jqXHR>,
    requestData?: any,
    configuration?: HandlingConfiguration
) => {
    const body = getResponseBody(xhr);
    const errorType = getErrorType(xhr);
    const options = getOptions(configuration, errorType, body);

    if (errorType === ApiErrorTypes.ObjectNotFound) {
        return objectNotFoundApiError(body, requestData, options);
    }

    if (errorType === ApiErrorTypes.UserFriendlyError) {
        return userFriendlyApiError(body, requestData, options);
    }
    if (
        errorType === ApiErrorTypes.UnknownError ||
        errorType === ApiErrorTypes.ForbiddenError ||
        errorType === ApiErrorTypes.InvalidParameters
    ) {
        return unknownApiError(requestData, options);
    }

    return null;
};

export const createGraphQLError = (error: GraphQLServerError, configuration?: HandlingConfiguration) => {
    const errorType = error.code as ApiErrorTypes;

    if (errorType === ApiErrorTypes.ObjectNotFound) {
        const errorBody: ObjectNotFoundErrorBody = {
            ErrorType: errorType,
            EntityName: error.entityName,
            ObjectKey: error.objectKey,
        };

        return objectNotFoundApiError(errorBody, undefined, getOptions(configuration, errorType, errorBody));
    }

    if (errorType === ApiErrorTypes.UserFriendlyError) {
        const errorBody: UserFriendlyExcBody = {
            ErrorType: errorType,
            ErrorKey: error.errorKey as keyof typeof commonResx,
        };
        return userFriendlyApiError(errorBody, undefined, getOptions(configuration, errorType, errorBody));
    }

    return null;
};

export const objectNotFoundApiError = (
    body: Partial<ObjectNotFoundErrorBody>,
    requestData?: any,
    options?: ErrorHandlingOptions
) =>
    ({
        type: apiMiddlewareActionTypes.API_OBJECT_NOT_FOUND,
        body: body as ObjectNotFoundErrorBody,
        requestData,
        ...options,
    } as const);

export const userFriendlyApiError = (body: UserFriendlyExcBody, requestData?: any, options?: ErrorHandlingOptions) =>
    ({
        type: apiMiddlewareActionTypes.API_USER_FRIENDLY_EXC,
        body,
        requestData,
        ...options,
    } as const);

export const unknownApiError = (requestData?: any, options?: ErrorHandlingOptions) =>
    ({
        type: ApiErrorTypes.UnknownError,
        requestData,
        ...options,
    } as const);

export function prepareApiErrorResponseOrLogError(
    e: Partial<JQuery.jqXHR> | GraphQLServerError,
    configuration?: HandlingConfiguration
) {
    let errorAction;
    if (e instanceof GraphQLServerError) {
        errorAction = createGraphQLError(e, configuration);
    } else {
        errorAction = createApiError(e, undefined, configuration);
    }

    if (errorAction) {
        return errorAction;
    }

    if (process.env.NODE_ENV !== "production") {
        throw e;
    }

    logError(e as Error);
}

export const isUserFriendlyError = (xhr: Partial<JQuery.jqXHR>, errorCode: string) => {
    if (getErrorType(xhr) === ApiErrorTypes.UserFriendlyError) {
        let body: UserFriendlyExcBody = getResponseBody(xhr);

        return body.ErrorKey === errorCode;
    }

    return false;
};
